import { useState } from 'react';
import { GoPlus } from 'react-icons/go';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@ewf/components/Dialog';
import { Button } from '@ewf/components/Button';
import { ModalContainer } from '@ewf/components/ModalUI/ModalContainer';
import { ConnectUserWallet } from '@ewf/components/ConnectUserWallet';
import { Lifting } from '@ewf/components/Lifting';
import { Lowering } from '@ewf/components/Lowering';
import { ConnectETHWallet } from '@ewf/components/ConnectETHWallet';
import { truncateBalance } from '@ewf/lib/utils';
import { ChainDropdown } from './ChainDropdown';
import { AddressBook } from '@ewf/components/AddressBook';
import { AddAccountFlow } from '@ewf/components/AddAccountFlow';
import { useConnectionStore } from '@ewf/stores/connection';
import { useAddressBookStore } from '@ewf/stores/address-book';
import { useBalanceStore } from '@ewf/stores/balance';
import { displayBalance } from '@ewf/utils/balance-helpers';
import { LIFTING_CHAINS, MARKETPLACE_CHAINS } from '@shared/types/enums';
import ewLogo from '@ewf/assets/logos/ew.svg';

export const Balance = (): JSX.Element => {
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [liftingStatus, setLiftingStatus] = useState<LIFTING_CHAINS | null>(null);
  const [isLoweringOpen, setIsLoweringOpen] = useState(false);
  const [addressBookStatus, setAddressBookStatus] = useState<LIFTING_CHAINS | null>(null);
  const [isAddAccountFlowOpen, setIsAddAccountFlowOpen] = useState(false);
  const [isETHWalletOpen, setIsETHWalletOpen] = useState(false);
  const isConnected = useConnectionStore((state) => state.isConnected);
  const balanceEWX = useBalanceStore((state) => state.balanceEWX);
  const isLoading = useBalanceStore((state) => state.isLoading);
  const accountsEWC = useAddressBookStore((state) => state.accountsEWC);
  const accountsETH = useAddressBookStore((state) => state.accountsETH);
  const resetErrorMsg = useAddressBookStore((state) => state.resetErrorMsg);
  const floatBalanceEWX = parseFloat(balanceEWX?.token || '0');
  const totalBalanceEWC = accountsEWC.reduce((acc, account) => acc + account.balance, 0);
  const totalBalanceETH = accountsETH.reduce((acc, account) => acc + account.balance, 0);
  const totalBalance = floatBalanceEWX + totalBalanceEWC + totalBalanceETH;

  const Header = (): JSX.Element => (
    <div className="mb-1 rounded-b-sm rounded-t-lg bg-gray-90 p-4 shadow-sm">
      {isConnected ? (
        <>
          <p className="mb-2 text-base text-font-subtle">Total EWT Balance</p>
          <div className="flex items-center gap-3">
            <img src={ewLogo} width={24} height={24} alt="EWT" className="inline" />
            {isLoading ? (
              <div className="block h-[24px] w-[64px] animate-pulse rounded bg-gray-80" />
            ) : (
              <span className="text-lg font-bold">
                {displayBalance(truncateBalance(totalBalance))}
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="text-base">Accounts</p>
      )}
    </div>
  );

  const WalletConnected = (): JSX.Element => (
    <>
      <p className="mb-3 text-sm text-font-subtle">Blockchain</p>
      {isLoading ? (
        <>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="mb-2 flex h-[28px] animate-pulse items-center rounded-full bg-gray-80"
            />
          ))}
        </>
      ) : (
        <ul>
          <div className="group mb-2 flex items-center">
            <p className="fade-in delay-50 mr-2 w-8 text-sm text-font-subtler transition group-hover:text-teal">
              EWX
            </p>
            <li className="fade-in delay-50 flex w-full items-center rounded-full border-l border-gray-80 bg-gray-80 py-1 pl-3 pr-1 transition group-hover:border-l group-hover:border-teal group-hover:shadow-sm">
              <span className="flex-grow pt-[2px] text-right">
                {displayBalance(truncateBalance(floatBalanceEWX))}
              </span>
              <ChainDropdown>
                <DropdownMenu.Item
                  onClick={() => setIsLoweringOpen(true)}
                  className="fade-in delay-50 cursor-pointer rounded-md px-2 py-1 text-font-subtle outline-none transition hover:bg-brand/10"
                >
                  Lower Tokens
                </DropdownMenu.Item>
              </ChainDropdown>
            </li>
          </div>

          {accountsEWC.length > 0 ? (
            <div className="group flex items-center">
              <p className="fade-in delay-50 mr-2 w-8 text-sm text-font-subtler transition group-hover:text-brand">
                EWC
              </p>
              <li className="fade-in delay-50 mb-2 flex w-full items-center rounded-full border-l border-gray-80 bg-gray-80 py-1 pl-3 pr-1 transition group-hover:border-l group-hover:border-brand group-hover:shadow-sm">
                <span className="flex-grow pt-[2px] text-right">
                  {displayBalance(truncateBalance(totalBalanceEWC))}
                </span>

                <ChainDropdown>
                  <DropdownMenu.Item
                    onClick={() => {
                      setLiftingStatus(MARKETPLACE_CHAINS.EWC);
                    }}
                    className="fade-in delay-50 -mb-1 cursor-pointer rounded-md px-2 py-1 text-font-subtle outline-none transition hover:bg-brand/10"
                  >
                    Lift Tokens
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => setAddressBookStatus(MARKETPLACE_CHAINS.EWC)}
                    className="fade-in delay-50 cursor-pointer rounded-md px-2 py-1 text-font-subtle outline-none transition hover:bg-brand/10"
                  >
                    Manage Accounts
                  </DropdownMenu.Item>
                </ChainDropdown>
              </li>
            </div>
          ) : null}

          {accountsETH.length > 0 ? (
            <div className="group flex items-center">
              <p className="fade-in delay-50 mr-2 w-8 text-sm text-font-subtler transition group-hover:text-[#7D97FF]">
                ETH
              </p>
              <li className="fade-in delay-50 mb-2 flex w-full items-center rounded-full border-l border-gray-80 bg-gray-80 py-1 pl-3 pr-1 transition group-hover:border-l group-hover:border-[#7D97FF] group-hover:shadow-sm">
                <span className="flex-grow pt-[2px] text-right">
                  {displayBalance(truncateBalance(totalBalanceETH))}
                </span>

                <ChainDropdown>
                  <DropdownMenu.Item
                    onClick={() => {
                      setLiftingStatus(MARKETPLACE_CHAINS.ETH);
                    }}
                    className="fade-in delay-50 -mb-1 cursor-pointer rounded-md px-2 py-1 text-font-subtle outline-none transition hover:bg-brand/10"
                  >
                    Lift Tokens
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => setAddressBookStatus(MARKETPLACE_CHAINS.ETH)}
                    className="fade-in delay-50 cursor-pointer rounded-md px-2 py-1 text-font-subtle outline-none transition hover:bg-brand/10"
                  >
                    Manage Accounts
                  </DropdownMenu.Item>
                </ChainDropdown>
              </li>
            </div>
          ) : null}

          {accountsEWC.length === 0 || accountsETH.length === 0 ? (
            <Button
              size="small"
              className="mt-1 flex h-[35px] w-full items-center justify-between gap-2 pr-1 text-sm bg-button-gradient"
              onClick={() => setIsAddAccountFlowOpen(true)}
            >
              Add Account
              <GoPlus color="#fff" className="flex h-[25px] w-[25px] items-center justify-center" />
            </Button>
          ) : null}
        </ul>
      )}
    </>
  );

  const WalletDisconnected = (): JSX.Element => (
    <Button
      onClick={() => setIsWalletOpen(true)}
      size="small"
      className="w-full bg-button-gradient"
    >
      Connect Wallet
    </Button>
  );

  return (
    <div className="w-full">
      <Header />
      <div className="rounded-b-lg rounded-t-sm bg-gray-90 px-4 py-3 shadow-sm">
        {isConnected && !isWalletOpen ? <WalletConnected /> : <WalletDisconnected />}
      </div>
      <ConnectUserWallet isOpen={isWalletOpen} setIsOpen={setIsWalletOpen} />
      <Lifting
        isOpen={!!liftingStatus}
        setIsOpen={(open) => {
          if (!open) setLiftingStatus(null);
        }}
        initialChain={liftingStatus ?? undefined}
      />
      <Lowering isOpen={isLoweringOpen} setIsOpen={setIsLoweringOpen} />
      <Dialog.Root open={isAddAccountFlowOpen}>
        <Dialog.Portal>
          <Dialog.Overlay>
            <Dialog.Content>
              <AddAccountFlow
                isOpen={isAddAccountFlowOpen}
                onClose={() => setIsAddAccountFlowOpen(false)}
              />
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root open={!!addressBookStatus}>
        <Dialog.Portal>
          <Dialog.Overlay>
            <Dialog.Content className="w-[500px]">
              <ModalContainer>
                <AddressBook
                  network={addressBookStatus!}
                  onClose={() => {
                    setAddressBookStatus(null);
                    resetErrorMsg();
                  }}
                />
              </ModalContainer>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root open={isETHWalletOpen}>
        <Dialog.Portal>
          <Dialog.Overlay>
            <Dialog.Content className="w-[500px]">
              <ModalContainer>
                <ConnectETHWallet onClose={() => setIsETHWalletOpen(false)} />
              </ModalContainer>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};
